import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "p-4 hover:bg-gray-50/80 dark:hover:bg-gray-800/30 transition-all duration-300 border-b border-gray-200/50 dark:border-gray-700/50" }
const _hoisted_2 = { class: "flex flex-col gap-3" }
const _hoisted_3 = { class: "flex items-start gap-3 w-full min-w-0" }
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = { class: "flex-1 min-w-0" }
const _hoisted_6 = { class: "flex flex-wrap items-center gap-2 mb-0.5" }
const _hoisted_7 = { class: "text-sm sm:text-base font-semibold text-primary-text dark:text-primary-dark-text truncate max-w-full" }
const _hoisted_8 = { class: "text-xs sm:text-sm text-secondary-text/80 dark:text-secondary-dark-text/80 truncate mb-1.5" }
const _hoisted_9 = { class: "flex flex-wrap gap-x-3 gap-y-1 text-xs text-secondary-text/70 dark:text-secondary-dark-text/70" }
const _hoisted_10 = {
  key: 0,
  class: "truncate"
}
const _hoisted_11 = { class: "flex items-center justify-start gap-2 sm:gap-1 mt-1 border-t border-gray-100 dark:border-gray-800 pt-3 sm:border-none sm:pt-0 sm:mt-0" }

import { computed, ref, watch } from 'vue'
import { Eye, Lock, LockOpen, Trash, UserRoundCheck } from 'lucide-vue-next'
import api from '@/services/api'
import type { Employee } from '@/services/employeeService'
import StatusBadge from './StatusBadge.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeCard',
  props: {
    employee: {}
  },
  emits: ["view", "activate", "deactivate", "approve", "delete"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const handleAction = (action: 'view' | 'activate' | 'deactivate' | 'approve' | 'delete', id: number) => {

  switch (action) {
    case 'view':
      emit('view', id)
      break
    case 'activate':
      emit('activate', id)
      break
    case 'approve':
      emit('approve', id)
      break
    case 'deactivate':
      emit('deactivate', id)
      break
    case 'delete':
      emit('delete', id)
      break
  }
}

const displayName = computed(() => {
  if (props.employee.first_name && props.employee.last_name) {
    return `${props.employee.first_name} ${props.employee.last_name}`
  }
  return props.employee.name
})

const employeeInitials = computed(() => {
  if (props.employee.first_name && props.employee.last_name) {
    return (props.employee.first_name[0] + props.employee.last_name[0]).toUpperCase()
  }
  const name = (props.employee.name || '').trim()
  if (!name) return 'EM'
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.substring(0, 2).toUpperCase()
})

const avatarLoadFailed = ref(false)

const profileImageUrl = computed(() => {
  const raw = String(props.employee.profile_picture || '').trim()
  if (!raw || raw.toLowerCase() === 'null') return ''

  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:image/') || raw.startsWith('blob:')) {
    return raw
  }

  const rawBase = String(api.defaults.baseURL || '').replace(/\/$/, '')
  const baseNoApi = rawBase.replace(/\/api$/i, '')

  if (raw.startsWith('/storage/')) return `${baseNoApi}${raw}`
  if (raw.startsWith('storage/')) return `${baseNoApi}/${raw}`

  return `${baseNoApi}/storage/${raw.replace(/^\//, '')}`
})

const shouldShowProfileImage = computed(() => Boolean(profileImageUrl.value) && !avatarLoadFailed.value)

watch(profileImageUrl, () => {
  avatarLoadFailed.value = false
})

const onProfileImageError = () => {
  avatarLoadFailed.value = true
}

const avatarBgClass = computed(() => {
  const colors = ['from-blue-400 to-blue-600', 'from-purple-400 to-purple-600', 'from-pink-400 to-pink-600', 'from-indigo-400 to-indigo-600', 'from-cyan-400 to-cyan-600', 'from-green-400 to-green-600', 'from-orange-400 to-orange-600']
  let hash = 0
  const str = displayName.value
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
})

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return 'N/A'
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (shouldShowProfileImage.value)
          ? (_openBlock(), _createElementBlock("img", {
              key: 0,
              src: profileImageUrl.value,
              alt: `${displayName.value} profile photo`,
              class: "h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover shadow-md flex-shrink-0 aspect-square",
              onError: onProfileImageError
            }, null, 40, _hoisted_4))
          : (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: _normalizeClass(["h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md bg-gradient-to-br flex-shrink-0", avatarBgClass.value]),
              "aria-label": "Default profile avatar"
            }, _toDisplayString(employeeInitials.value), 3)),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("h3", _hoisted_7, _toDisplayString(displayName.value), 1),
            _createVNode(StatusBadge, {
              status: __props.employee.status,
              size: "sm",
              class: "flex-shrink-0"
            }, null, 8, ["status"])
          ]),
          _createElementVNode("p", _hoisted_8, _toDisplayString(__props.employee.email), 1),
          _createElementVNode("div", _hoisted_9, [
            (__props.employee.phone)
              ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(__props.employee.phone), 1))
              : _createCommentVNode("", true),
            _createElementVNode("span", null, _toDisplayString(formatDate(__props.employee.updated_at)), 1)
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (handleAction('view', __props.employee.id))),
          class: "p-2 bg-gray-50 sm:bg-transparent dark:bg-gray-800 sm:dark:bg-transparent rounded-md text-blue-600 hover:text-primary hover:bg-primary/10 transition-all duration-200",
          title: "View"
        }, [
          _createVNode(_unref(Eye), { class: "h-4 w-4 sm:h-5 sm:w-5" })
        ]),
        (__props.employee.status === 'active')
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (handleAction('deactivate', __props.employee.id))),
              class: "p-2 bg-gray-50 sm:bg-transparent dark:bg-gray-800 sm:dark:bg-transparent rounded-md text-warning/70 hover:text-warning hover:bg-warning/10 transition-all duration-200",
              title: "Deactivate"
            }, [
              _createVNode(_unref(Lock), { class: "h-4 w-4 sm:h-5 sm:w-5" })
            ]))
          : (__props.employee.status === 'inactive')
            ? (_openBlock(), _createElementBlock("button", {
                key: 1,
                onClick: _cache[2] || (_cache[2] = ($event: any) => (handleAction('activate', __props.employee.id))),
                class: "p-2 bg-gray-50 sm:bg-transparent dark:bg-gray-800 sm:dark:bg-transparent rounded-md text-success/70 hover:text-success hover:bg-success/10 transition-all duration-200",
                title: "Activate"
              }, [
                _createVNode(_unref(LockOpen), { class: "h-4 w-4 sm:h-5 sm:w-5" })
              ]))
            : (__props.employee.status === 'pending')
              ? (_openBlock(), _createElementBlock("button", {
                  key: 2,
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (handleAction('approve', __props.employee.id))),
                  class: "p-2 bg-gray-50 sm:bg-transparent dark:bg-gray-800 sm:dark:bg-transparent rounded-md text-success/70 hover:text-success hover:bg-success/10 transition-all duration-200",
                  title: "Approve"
                }, [
                  _createVNode(_unref(UserRoundCheck), { class: "h-4 w-4 sm:h-5 sm:w-5" })
                ]))
              : _createCommentVNode("", true),
        _createElementVNode("button", {
          onClick: _cache[4] || (_cache[4] = ($event: any) => (handleAction('delete', __props.employee.id))),
          class: "p-2 bg-gray-50 sm:bg-transparent dark:bg-gray-800 sm:dark:bg-transparent rounded-md text-danger/70 hover:text-danger hover:bg-danger/10 transition-all duration-200",
          title: "Delete"
        }, [
          _createVNode(_unref(Trash), { class: "h-4 w-4 sm:h-5 sm:w-5" })
        ])
      ])
    ])
  ]))
}
}

})